import AddIcon from "@mui/icons-material/Add";
import * as React from "react";
import { useForm, Controller } from "react-hook-form";

import { RobotoButton } from "@/shared/components/RobotoButton";
import { RobotoTypography } from "@/shared/components/RobotoTypography";
import {
  getCorrespondingGeoType,
  isLatitudeType,
  isLongitudeType,
  MessagePathNode,
} from "@/shared/domain/topics";
import {
  actions,
  MapPanelState,
  useVizDispatch,
} from "@/shared/state/visualization";

import { DataSourceSelector } from "../../controls/DataSourceSelector";

import styles from "./AddPath.module.css";

interface AddMapPathProps {
  state: MapPanelState;
}

interface AddPathFormData {
  latitude: MessagePathNode | null;
  longitude: MessagePathNode | null;
}

export function AddPath({ state }: AddMapPathProps) {
  const vizStateDispatch = useVizDispatch();
  const [isAddingNewPath, setIsAddingNewPath] = React.useState(true);

  const form = useForm<AddPathFormData>({
    defaultValues: {
      latitude: null,
      longitude: null,
    },
    // Validate fields on every change
    mode: "onChange",
  });

  const latitude = form.watch("latitude");
  const longitude = form.watch("longitude");

  const onSubmit = (data: AddPathFormData) => {
    if (data.latitude && data.longitude) {
      // When adding data, we ensure the data is in [lat, lon] pairs
      const pathData = [
        data.latitude.toTopicData(),
        data.longitude.toTopicData(),
      ];
      vizStateDispatch(actions.addPathToMapPanel(state.id, pathData));
      setIsAddingNewPath(false);
      form.reset();
    }
  };

  const handleCancel = () => {
    setIsAddingNewPath(false);
    form.reset();
  };

  function isLatitude(messagePathNode: MessagePathNode) {
    if (longitude) {
      return (
        messagePathNode.data.canonical_data_type ===
        getCorrespondingGeoType(longitude.data.canonical_data_type)
      );
    }
    return isLatitudeType(messagePathNode.data.canonical_data_type);
  }

  function isLongitude(messagePathNode: MessagePathNode) {
    if (latitude) {
      return (
        messagePathNode.data.canonical_data_type ===
        getCorrespondingGeoType(latitude.data.canonical_data_type)
      );
    }
    return isLongitudeType(messagePathNode.data.canonical_data_type);
  }

  if (isAddingNewPath) {
    return (
      <div className={styles.formContainer}>
        <RobotoTypography variant="overline">New path</RobotoTypography>
        <form
          className={styles.form}
          onSubmit={(e) => {
            void form.handleSubmit(onSubmit)(e);
          }}
        >
          <div>
            <RobotoTypography component={"label"} variant="subtitle2">
              Latitude
            </RobotoTypography>
            <Controller
              name="latitude"
              control={form.control}
              rules={{ required: true }}
              render={({ field }) => (
                <DataSourceSelector
                  onChange={(messagePathNode) => {
                    field.onChange(messagePathNode);
                  }}
                  optionFilter={isLatitude}
                />
              )}
            />
          </div>

          <div>
            <RobotoTypography component={"label"} variant="subtitle2">
              Longitude
            </RobotoTypography>
            <Controller
              name="longitude"
              control={form.control}
              rules={{ required: true }}
              render={({ field }) => (
                <DataSourceSelector
                  onChange={(messagePathNode) => {
                    field.onChange(messagePathNode);
                  }}
                  optionFilter={isLongitude}
                />
              )}
            />
          </div>

          <div className={styles.positionRight}>
            <RobotoButton
              eventName={"AddMapPathCanceled"}
              onClick={handleCancel}
              size="small"
              type="button"
            >
              Cancel
            </RobotoButton>
            <RobotoButton
              eventName={"AddMapPathCompleted"}
              type="submit"
              variant="contained"
              size="small"
              disabled={!form.formState.isValid}
            >
              Add Path
            </RobotoButton>
          </div>
        </form>
      </div>
    );
  }

  return (
    <RobotoButton
      eventName={"AddMapPathClicked"}
      onClick={() => setIsAddingNewPath(true)}
      startIcon={<AddIcon />}
      size="small"
    >
      Add Path
    </RobotoButton>
  );
}
